<script setup lang="ts">
import { Plus, EditPencil, Trash } from "@iconoir/vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import LeadFormModal from "./LeadFormModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "Lead" });

const { t, te } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

interface Lead {
  id: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  website: string;
  industry: string;
  status: string;
  score: number;
  tags: string[];
  taxCode: string;
  source: string;
  sourceKey: string;
  sourceURL: string;
  assigned?: { id: string; fullName: string } | null;
  createTime: number;
}

const defaultFilter = {
  keyword: "",
  status: "",
  source: "",
  sort: "",
  tag: "",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<Lead, typeof defaultFilter>({
  endpoint: "/leads",
  defaultFilter,
});

const { del, errorMsg } = useCrud<Lead>({ endpoint: "/leads" });

const formRef = ref<InstanceType<typeof LeadFormModal> | null>(null);

const statusOptions = computed(() => [
  { value: "", label: t("lead.statusAll") },
  ...["new", "contacted", "replied", "qualified", "won", "lost"].map((value) => ({
    value,
    label: t(`lead.status_${value}`),
  })),
]);

const sortOptions = computed(() => [
  { value: "", label: t("sort.newestFirst") },
  { value: "score", label: t("lead.sortScore") },
]);

const sourceOptions = computed(() => [
  { value: "", label: t("lead.sourceAll") },
  { value: "manual", label: t("lead.sourceManual") },
  { value: "crawler", label: t("lead.sourceCrawler") },
  { value: "import", label: t("lead.sourceImport") },
]);

const statusIntent: Record<string, string> = {
  new: "info",
  contacted: "neutral",
  replied: "warning",
  qualified: "warning",
  won: "positive",
  lost: "negative",
};

// zalo-* tags are outcomes rather than segments, so they read as status: sent
// is good, a dead number is a fact, a failed send is something to look at.
// Everything else is a segment and stays neutral.
const tagIntent: Record<string, string> = {
  "zalo-sent": "positive",
  "zalo-not-found": "negative",
  "zalo-failed": "warning",
};

// The tag filter takes free text, so the array the input holds has to become
// the comma list ListLead's `tag` param expects.
const filterTags = computed({
  get: () => (filter.tag ? filter.tag.split(",") : []),
  set: (value: string[]) => {
    filter.tag = value.join(",");
  },
});

// Phones are stored as digits with a country code; the table reads better with
// the local 0-prefixed form people actually dial.
function phoneLabel(phone: string) {
  if (!phone) return "—";
  return phone.startsWith("84") ? `0${phone.slice(2)}` : phone;
}

// Known tags get a translated label; anything the team invented shows as typed.
function tagLabel(tag: string) {
  const key = `lead.tag_${tag.replace(/-/g, "_")}`;
  return te(key) ? t(key) : tag;
}

function sourceLabel(row: Lead) {
  if (row.source === "crawler") return t("lead.sourceSystem");
  if (row.source === "import") return t("lead.sourceImport");
  return t("lead.sourceManual");
}

// tel: wants the international form even though the label shows the local one,
// so a tap dials correctly from anywhere.
function telHref(phone: string) {
  return phone ? `tel:+${phone}` : "";
}

async function confirmDelete(row: Lead) {
  const ok = await alert.negative({
    title: t("lead.deleteConfirmTitle"),
    description: t("lead.deleteConfirmBody", { name: row.companyName }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id);
    toast.success({ title: t("message.deleteSuccess") });
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => [filter.status, filter.source, filter.sort, filter.tag],
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);

onMounted(async () => {
  setTitle(t("lead.title"));
  await load();
});
</script>

<template>
  <ListPage
    :title="t('lead.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('lead.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.LeadsWrite)"
        variant="primary"
        size="small"
        @click="formRef?.open()"
      >
        <Plus class="icon" />
        {{ t("lead.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty :title="t('lead.emptyTitle')" :subtitle="t('lead.emptySubtitle')">
        <template #action>
          <Button
            v-if="can(AdminPermission.LeadsWrite)"
            variant="primary"
            size="small"
            @click="formRef?.open()"
          >
            <Plus class="icon" />
            {{ t("lead.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('lead.emptySearchTitle')"
        :subtitle="t('lead.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect v-model="filter.status" :options="statusOptions" size="sm" class="w-full md:flex-1" />
      <FormSelect v-model="filter.source" :options="sourceOptions" size="sm" class="w-full md:flex-1" />
      <FormSelect v-model="filter.sort" :options="sortOptions" size="sm" class="w-full md:flex-1" />
    </template>

    <template #filter-right>
      <FormTagsInput
        v-model="filterTags"
        :placeholder="t('lead.filterTag')"
        size="sm"
        class="w-full md:w-1/2"
      />
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'companyName', label: t('lead.company'), class: 'md:min-w-[240px]' },
          { key: 'status', label: t('field.status'), class: 'min-w-[112px]' },
          { key: 'phone', label: t('field.phone'), class: 'min-w-[144px]' },
          { key: 'tags', label: t('field.tags'), class: 'min-w-[180px] hidden md:table-cell' },
          { key: 'createTime', label: t('field.createTime'), class: 'min-w-[160px] hidden lg:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
        ]"
        :items="items"
        @row-click="(row: Lead) => navigateTo(`/leads/${row.id}`)"
      >
        <template #cell-companyName="{ row }">
          <div class="flex min-w-0 flex-col gap-3xsmall">
            <span class="content-neutral-strong truncate">{{ row.companyName }}</span>
            <span class="text-body-small-tertiary content-neutral-medium truncate">
              {{ sourceLabel(row) }}
            </span>
          </div>
        </template>

        <template #cell-status="{ row }">
          <Badge :intent="statusIntent[row.status] || 'neutral'" emphasis="medium" size="small">
            {{ t(`lead.status_${row.status}`) }}
          </Badge>
        </template>

        <template #cell-phone="{ row }">
          <a v-if="row.phone" :href="telHref(row.phone)" @click.stop>{{ phoneLabel(row.phone) }}</a>
          <span v-else class="content-neutral-medium">—</span>
        </template>

        <template #cell-tags="{ row }">
          <div v-if="row.tags?.length" class="flex flex-wrap items-center gap-3xsmall">
            <Badge
              v-for="tag in row.tags"
              :key="tag"
              :intent="tagIntent[tag] || 'neutral'"
              emphasis="medium"
              size="small"
            >
              {{ tagLabel(tag) }}
            </Badge>
          </div>
          <span v-else class="content-neutral-medium">—</span>
        </template>

        <template #cell-createTime="{ row }">
          <span class="lead__figure">{{ d(row.createTime, "short") }}</span>
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.LeadsWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click.stop="formRef?.open(row.id)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="can(AdminPermission.LeadsDelete)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <LeadFormModal ref="formRef" @saved="load()" />
</template>

<style scoped lang="scss">
// Dates line up digit for digit down the column.
.lead__figure {
  font-variant-numeric: tabular-nums;
}
</style>
